import type {
  AnnulOperation,
  BranchChangeset,
  ChangesetHistory,
  ConfigurationOperation,
} from '@festina/configuration-general-types/configuration'
import {
  LayerKey,
  resolveTopologicalPathForBlock,
  type Tree,
} from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'

import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import {
  findActiveAnnulments,
  indexAnnulmentOperations,
  indexChangesetOperations,
} from '../annulOperations/helpers'
import {
  ensureIndexedBlockLayerEntity,
  pushSorted,
  userActionsForOperation,
} from '../helpers'
import type {
  IndexedOperations,
  OperationWithSourceInfo,
  WorkspaceOperationOf,
} from '../types'

/**
 * Indexes the operations of a single configuration item type by block, layer and entity
 * id, resolving the annulments that apply to every version and keeping the versions of
 * each entity sorted. Draft operations are never annulled, as an annulment can only
 * target an operation that already exists on the branch.
 */
export const constructIndexedOperations = <
  T extends ConfigurationOperation,
  V extends IndexedOperations<T>,
>({
  configurationChangesets,
  draftOperations,
  draftAnnulOperations,
  blockTopology,
  isIndexedOperation,
  getEntityId,
  createDefault,
  findIndexFn,
  postProcessEntity,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  draftOperations: Optional<WorkspaceOperationOf<T>[]>
  draftAnnulOperations: Optional<WorkspaceOperationOf<AnnulOperation>[]>
  blockTopology: Tree
  isIndexedOperation: (operation: ConfigurationOperation) => operation is T
  /** Identity of the entity the operation belongs to, such as a template or mapping id. */
  getEntityId: (operation: T) => Optional<string>
  createDefault: (entityId: string) => V
  findIndexFn: (
    topologicalBlockPath: string[],
  ) => (
    forOperation: OperationWithSourceInfo<T>,
  ) => (compareTo: OperationWithSourceInfo<T>) => boolean
  /**
   * Refreshes the fields derived from the indexed versions, such as the entity of the
   * last create operation. Called once all operations have been added.
   */
  postProcessEntity: (indexedEntity: V) => void
}): ConfigurationItemsByBlock<V> => {
  const rootBlock = blockTopology.name
  const changesetAnnulments = indexAnnulmentOperations({
    changesets: configurationChangesets ?? [],
    baseBlock: rootBlock,
  })
  const operationIndexes = indexChangesetOperations(
    configurationChangesets ?? [],
  )

  const allResults: ConfigurationItemsByBlock<V> = new Map()

  const pushToResult = ({
    operation,
    isDraft,
    layer,
    block,
    changesetHistory,
  }: {
    operation: T
    isDraft: boolean
    layer: LayerKey
    block: string
    changesetHistory: Optional<ChangesetHistory>
  }) => {
    const entityId = getEntityId(operation)
    assertNotEmpty(entityId)

    const indexedEntity = ensureIndexedBlockLayerEntity({
      allResults,
      block,
      layer,
      entityId,
      createDefault: () => createDefault(entityId),
    })

    const operationWithoutAnnulments = {
      isDraft,
      layer,
      block,
      internalOperation: operation,
      changesetHistory,
      actions: userActionsForOperation(operation, changesetHistory),
    }

    pushSorted({
      array: indexedEntity.operations,
      element: {
        ...operationWithoutAnnulments,
        annulOperations: isDraft
          ? []
          : findActiveAnnulments({
              operation: operationWithoutAnnulments,
              changesetAnnulments,
              operationIndexes,
              workspaceAnnulments: draftAnnulOperations ?? [],
            }),
      },
      findIndexFn: findIndexFn(
        resolveTopologicalPathForBlock(block, blockTopology),
      ),
    })
  }

  ;(configurationChangesets ?? []).forEach((branchChangeset) => {
    const layer = new LayerKey({
      tenantId: branchChangeset.changeset?.tenantId,
      layerId: branchChangeset.changeset?.layer,
    })
    const block = branchChangeset.block ?? rootBlock

    ;(branchChangeset.changeset?.operations ?? [])
      .filter(notEmpty)
      .filter(isIndexedOperation)
      .forEach((operation) =>
        pushToResult({
          operation,
          layer,
          block,
          isDraft: false,
          changesetHistory: branchChangeset.changesetHistory,
        }),
      )
  })

  draftOperations?.forEach((draftOperation) =>
    pushToResult({
      operation: draftOperation.operation,
      layer: new LayerKey({
        tenantId: draftOperation.tenantId,
        layerId: draftOperation.layer,
      }),
      block: draftOperation.block ?? rootBlock,
      isDraft: true,
      changesetHistory: undefined,
    }),
  )

  for (const blockLayers of allResults.values()) {
    for (const layerResults of blockLayers.values()) {
      for (const indexedEntity of layerResults.values()) {
        indexedEntity.isDraft = indexedEntity.operations.some(
          (operation) => operation.isDraft,
        )

        postProcessEntity(indexedEntity)
      }
    }
  }

  return allResults
}
